import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCellDef, MatColumnDef, MatHeaderCellDef, MatHeaderRowDef, MatRowDef } from '@angular/material/table';
import {
  FwAvatarComponent,
  FwBadgeComponent,
  FwButtonComponent,
  FwCell,
  FwContainedInputComponent,
  FwDialogActionsComponent,
  FwDialogComponent,
  FwDialogContentComponent,
  FwDialogHeaderComponent,
  FwHeaderCell,
  FwHeaderRow,
  FwIconButtonComponent,
  FwMenuItemComponent,
  FwMultiSelectMenuComponent,
  FwProgressBarComponent,
  FwRow,
  FwSectionHeadingComponent,
  FwTabComponent,
  FwTableComponent,
  FwTabPanelComponent,
  FwTabsComponent,
  FwTooltipDirective,
} from '@flywheel-io/vision';
import isEqual from 'lodash/isEqual';
import { combineLatest, finalize, merge } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import { SiteRole } from '../../shared/enums/site-role.enum';
import { GearPermissions, GearSeries } from '../../shared/models/gear.model';
import { ProjectWithStats } from '../../shared/models/project.model';
import { User } from '../../shared/models/user.model';
import { UserAvatarContentPipe } from '../../shared/pipes/user-avatar-content.pipe';
import { UserAvatarUrlPipe } from '../../shared/pipes/user-avatar-url.pipe';
import { UserInitialPipe } from '../../shared/pipes/user-initial.pipe';
import { GearService } from '../gear.service';

interface CachedValue {
  isRestricted: boolean
  projects: Record<string, boolean>
  users: Record<string, boolean>
}

@Component({
  host: {
    'test-id': 'gear-access-dialog',
  },
  selector: 'gear-access-dialog',
  styleUrls: ['./gear-access-dialog.component.scss'],
  templateUrl: './gear-access-dialog.component.html',
  imports: [
    FwAvatarComponent,
    FwBadgeComponent,
    FwButtonComponent,
    FwCell,
    FwContainedInputComponent,
    FwDialogActionsComponent,
    FwDialogComponent,
    FwDialogContentComponent,
    FwDialogHeaderComponent,
    FwHeaderCell,
    FwHeaderRow,
    FwIconButtonComponent,
    FwMenuItemComponent,
    FwMultiSelectMenuComponent,
    FwProgressBarComponent,
    FwRow,
    FwSectionHeadingComponent,
    FwTabComponent,
    FwTabPanelComponent,
    FwTableComponent,
    FwTabsComponent,
    FwTooltipDirective,
    MatCellDef,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderRowDef,
    MatRowDef,
    ReactiveFormsModule,
    UserAvatarContentPipe,
    UserAvatarUrlPipe,
    UserInitialPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GearAccessDialogComponent {
  readonly ref = inject(DialogRef);
  private gearService = inject(GearService);
  private injector = inject(Injector);
  private profileService = inject(ProfileService);
  private projectsService = inject(ProjectsService);

  private readonly loadedData = toSignal(
    combineLatest([
      this.profileService.getProfile().pipe(filter(Boolean)),
      this.projectsService.getGroups(),
      this.projectsService.getProjects(),
      this.projectsService.getUsers(),
    ]),
  );

  form = new FormGroup({
    isRestricted: new FormControl({ value: false, disabled: true }),
    projects: new FormControl({}),
    users: new FormControl({}),
  })
  readonly gearLabel = signal('')
  readonly groupLabels = signal<Record<string, string>>({})
  initialCachedValue: CachedValue
  readonly isDeveloper = signal(false)
  readonly isSaving = signal(false)
  readonly isSiteAdmin = signal(false)
  series: GearSeries

  allUsers: User[] = []
  readonly addedUsers = signal<User[]>([])
  addedUsersColumns = ['name', 'email', 'actions']
  readonly userPickerControl = new FormControl<string[]>([])
  allProjects: ProjectWithStats[] = []
  readonly addedProjects = signal<ProjectWithStats[]>([])
  addedProjectsColumns = ['label', 'group', 'actions']
  readonly projectPickerControl = new FormControl<string[]>([])

  readonly isAddedUsersTableScrollable = signal(false)
  readonly isAddedProjectsTableScrollable = signal(false)

  private readonly addedUsersTableRef = viewChild('addedUsersTable', { read: ElementRef });
  private readonly addedProjectsTableRef = viewChild('addedProjectsTable', { read: ElementRef });
  private readonly projectPickerRef = viewChild<FwMultiSelectMenuComponent>('projectPicker');
  private readonly userPickerRef = viewChild<FwMultiSelectMenuComponent>('userPicker');

  readonly availableUsers = computed(() => {
    const addedIds = new Set(this.addedUsers().map((u) => u._id));
    return this.allUsers.filter((u) => !addedIds.has(u._id));
  });

  readonly availableProjects = computed(() => {
    const addedIds = new Set(this.addedProjects().map((p) => p._id));
    return this.allProjects.filter((p) => !addedIds.has(p._id));
  });

  readonly selectedProjectLabels = computed(() => this.addedProjects().map((p) => p.label));

  readonly selectedUserLabels = computed(() => this.addedUsers().map((u) => `${u.firstname} ${u.lastname}`));

  readonly isLoading = computed(() => !this.loadedData());

  constructor() {
    const data = inject<{
        label: string;
        series: GearSeries;
    }>(DIALOG_DATA) ?? { label: '', series: {} as GearSeries };

    this.series = data.series || {} as GearSeries;
    this.gearLabel.set(data.label || '');

    effect(() => {
      const loaded = this.loadedData();
      if (!loaded) {return;}
      const [profile, groups, projects, users] = loaded;

      const isSiteAdmin = Boolean(profile && SiteRole.isSiteAdmin(profile));
      const isDeveloper = Boolean(profile && SiteRole.isDeveloper(profile));
      this.isSiteAdmin.set(isSiteAdmin);
      this.isDeveloper.set(isDeveloper);
      if (isSiteAdmin || isDeveloper) {
        this.form.get('isRestricted').enable();
      }
      const labelsMap: Record<string, string> = {};
      for (const group of groups) {
        labelsMap[group._id] = group.label;
      }
      this.groupLabels.set(labelsMap);
      const projectIds = {};
      for (const id of (this.series.permissions?.projects || [])) {
        projectIds[id] = true;
      }
      this.allProjects = projects;
      const userIds = {};
      for (const id of (this.series.permissions?.users || [])) {
        userIds[id] = true;
      }
      this.allUsers = users;
      const initialAddedProjects = projects.filter((p) => projectIds[p._id]);
      const initialAddedUsers = users.filter((u) => userIds[u._id]);
      this.addedProjects.set(initialAddedProjects);
      this.addedUsers.set(initialAddedUsers);
      this.form.setValue({
        isRestricted: this.series.is_restricted || false,
        projects: projectIds,
        users: userIds,
      });
      if (initialAddedProjects.length === projects.length) {
        this.projectPickerControl.disable();
      }
      if (initialAddedUsers.length === users.length) {
        this.userPickerControl.disable();
      }
      this.initialCachedValue = JSON.parse(JSON.stringify(this.form.getRawValue()));
    });

    effect((onCleanup) => {
      this.addedUsers();
      this.addedProjects();
      const ref = afterNextRender({
        read: () => {
          const usersEl = this.addedUsersTableRef()?.nativeElement as HTMLElement | undefined;
          this.isAddedUsersTableScrollable.set(usersEl ? usersEl.scrollHeight > usersEl.clientHeight : false);
          const projectsEl = this.addedProjectsTableRef()?.nativeElement as HTMLElement | undefined;
          this.isAddedProjectsTableScrollable.set(projectsEl ? projectsEl.scrollHeight > projectsEl.clientHeight : false);
        },
      }, { injector: this.injector });
      onCleanup(() => ref.destroy());
    });
  }

  // getRawValue() needed: disabled control values are omitted from form.value
  protected readonly isRestricted = toSignal(
    merge(
      this.form.controls.isRestricted.valueChanges,
      this.form.controls.isRestricted.statusChanges,
    ).pipe(map(() => this.form.getRawValue().isRestricted ?? false)),
    { initialValue: false },
  );

  protected hasChanged(): boolean {
    return !isEqual(this.initialCachedValue, this.form.getRawValue());
  }

  protected reset(): void {
    this.form.setValue(JSON.parse(JSON.stringify(this.initialCachedValue)));
    this.addedProjects.set(this.allProjects.filter((p) => this.initialCachedValue.projects[p._id]));
    this.projectPickerControl.setValue([]);
    this.availableProjects().length === 0 ? this.projectPickerControl.disable() : this.projectPickerControl.enable();
    this.addedUsers.set(this.allUsers.filter((u) => this.initialCachedValue.users[u._id]));
    this.userPickerControl.setValue([]);
    this.availableUsers().length === 0 ? this.userPickerControl.disable() : this.userPickerControl.enable();
  }

  protected addSelectedUsers(): void {
    const pickedIds: string[] = this.userPickerControl.value || [];
    if (pickedIds.length === 0) {
      return;
    }
    this.userPickerControl.setValue([]);
    const current = { ...this.form.value.users };
    const newAddedUsers = [...this.addedUsers()];
    for (const id of pickedIds) {
      current[id] = true;
      const user = this.allUsers.find((u) => u._id === id);
      if (user && !newAddedUsers.find((u) => u._id === id)) {
        newAddedUsers.push(user);
      }
    }
    this.addedUsers.set(newAddedUsers);
    this.form.get('users').setValue(current);
    if (this.availableUsers().length === 0) {
      this.userPickerRef()?.trigger.close();
      setTimeout(() => this.userPickerControl.disable());
    }
  }

  protected removeAddedUser(id: string): void {
    this.addedUsers.set(this.addedUsers().filter((u) => u._id !== id));
    const current = { ...this.form.value.users };
    delete current[id];
    this.form.get('users').setValue(current);
    this.userPickerControl.enable();
  }

  protected addSelectedProjects(): void {
    const pickedIds: string[] = this.projectPickerControl.value || [];
    if (pickedIds.length === 0) {
      return;
    }
    this.projectPickerControl.setValue([]);
    const current = { ...this.form.value.projects };
    const newAddedProjects = [...this.addedProjects()];
    for (const id of pickedIds) {
      current[id] = true;
      const project = this.allProjects.find((p) => p._id === id);
      if (project && !newAddedProjects.find((p) => p._id === id)) {
        newAddedProjects.push(project);
      }
    }
    this.addedProjects.set(newAddedProjects);
    this.form.get('projects').setValue(current);
    if (this.availableProjects().length === 0) {
      this.projectPickerRef()?.trigger.close();
      setTimeout(() => this.projectPickerControl.disable());
    }
  }

  protected removeAddedProject(id: string): void {
    this.addedProjects.update(projects => projects.filter((p) => p._id !== id));
    const current = { ...this.form.value.projects };
    delete current[id];
    this.form.get('projects').setValue(current);
    this.projectPickerControl.enable();
  }

  protected save(): void {
    this.isSaving.set(true);
    this.gearService.updateGearAccess(
      { is_restricted: this.form.getRawValue().isRestricted, name: this.series.name } as GearSeries,
      { projects: this.getSelectedProjectIds(), users: this.getSelectedUserIds() } as GearPermissions,
    ).pipe(finalize(() => this.isSaving.set(false))).subscribe(() => {
      this.initialCachedValue = this.form.getRawValue() as CachedValue;
      this.gearService.reloadGear$.next(this.series.name);
    });
  }

  private getSelectedProjectIds(): string[] {
    return Object.keys(this.form.value.projects);
  }

  private getSelectedUserIds(): string[] {
    return Object.keys(this.form.value.users);
  }

}
